import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "editor", "autocomplete", "tableList", "tableFilter", "form",
    "schemaPanel", "schemaBody", "schemaTableName"
  ]
  static values = { schema: Object, exportUrl: String }

  connect() {
    this.schemaData = this.schemaValue || {}
    this.tableNames = Object.keys(this.schemaData).sort()
    this.autocompleteIndex = -1
    this.autocompleteItems = []
    this.showingAutocomplete = false

    this.sqlKeywords = [
      "SELECT", "FROM", "WHERE", "AND", "OR", "NOT", "IN", "IS", "NULL",
      "LIKE", "ILIKE", "BETWEEN", "EXISTS", "CASE", "WHEN", "THEN", "ELSE", "END",
      "JOIN", "INNER", "LEFT", "RIGHT", "FULL", "OUTER", "CROSS", "ON",
      "GROUP", "BY", "ORDER", "ASC", "DESC", "HAVING", "LIMIT", "OFFSET",
      "AS", "DISTINCT", "COUNT", "SUM", "AVG", "MIN", "MAX", "COALESCE",
      "CAST", "EXTRACT", "DATE_TRUNC", "NOW", "CURRENT_TIMESTAMP",
      "TRUE", "FALSE", "UNION", "ALL", "EXCEPT", "INTERSECT",
      "WITH", "RECURSIVE", "OVER", "PARTITION", "WINDOW",
      "FILTER", "ARRAY_AGG", "STRING_AGG", "JSON_AGG", "JSONB_AGG",
      "LATERAL", "GENERATE_SERIES", "UNNEST", "ANY", "SOME",
      "ROW_NUMBER", "RANK", "DENSE_RANK", "LAG", "LEAD",
      "FIRST_VALUE", "LAST_VALUE", "NTH_VALUE"
    ]

    this.aliases = {}
  }

  // Turbo form lifecycle — show loading state
  onSubmitStart() {
    this.hideAutocomplete()
    const status = document.getElementById("sql-status")
    if (status) {
      status.textContent = "Running..."
      status.classList.add("text-core-purple-600")
    }
    const results = document.getElementById("sql-results")
    if (results) {
      results.innerHTML = `
        <div class="flex items-center justify-center h-full text-support-light-500 text-sm">
          <div class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
            Executing query...
          </div>
        </div>
      `
    }
  }

  onSubmitEnd() {
    const status = document.getElementById("sql-status")
    if (status) status.classList.remove("text-core-purple-600")
  }

  // Keyboard handling
  handleKeydown(event) {
    if (this.showingAutocomplete) {
      if (event.key === "Tab" || event.key === "Enter") {
        event.preventDefault()
        this.acceptAutocomplete()
        return
      }
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.moveAutocomplete(1)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        this.moveAutocomplete(-1)
        return
      }
      if (event.key === "Escape") {
        event.preventDefault()
        this.hideAutocomplete()
        return
      }
    }

    // Cmd/Ctrl + Enter to submit the form
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.formTarget.requestSubmit()
      return
    }

    // Tab for indent (when autocomplete not shown)
    if (event.key === "Tab" && !this.showingAutocomplete) {
      event.preventDefault()
      const editor = this.editorTarget
      const start = editor.selectionStart
      const end = editor.selectionEnd
      editor.value = editor.value.substring(0, start) + "  " + editor.value.substring(end)
      editor.selectionStart = editor.selectionEnd = start + 2
    }
  }

  handleInput() {
    this.parseAliases()
    this.updateAutocomplete()
  }

  // Alias parsing
  parseAliases() {
    const text = this.editorTarget.value
    this.aliases = {}

    const aliasPattern = /(?:FROM|JOIN)\s+(\w+)\s+(?:AS\s+)?(\w+)/gi
    let match
    while ((match = aliasPattern.exec(text)) !== null) {
      const tableName = match[1].toLowerCase()
      const alias = match[2].toLowerCase()
      const keywords = ["where", "on", "and", "or", "left", "right", "inner", "outer", "full", "cross", "join", "group", "order", "limit", "having", "union", "set", "as"]
      if (!keywords.includes(alias) && this.tableNames.includes(tableName)) {
        this.aliases[alias] = tableName
      }
    }
  }

  // Autocomplete
  updateAutocomplete() {
    const editor = this.editorTarget
    const cursorPos = editor.selectionStart
    const textBeforeCursor = editor.value.substring(0, cursorPos)

    const wordMatch = textBeforeCursor.match(/[\w.]+$/)
    if (!wordMatch || wordMatch[0].length < 1) {
      this.hideAutocomplete()
      return
    }

    const currentWord = wordMatch[0]
    const suggestions = this.getSuggestions(currentWord, textBeforeCursor)

    if (suggestions.length === 0) {
      this.hideAutocomplete()
      return
    }

    this.showAutocompleteDropdown(suggestions, currentWord)
  }

  getSuggestions(currentWord, textBeforeCursor) {
    const parts = currentWord.split(".")
    const suggestions = []

    if (parts.length === 2) {
      const prefix = parts[0].toLowerCase()
      const colFilter = parts[1].toLowerCase()

      let tableName = null
      if (this.aliases[prefix]) {
        tableName = this.aliases[prefix]
      } else if (this.schemaData[prefix]) {
        tableName = prefix
      }

      if (tableName && this.schemaData[tableName]) {
        for (const col of this.schemaData[tableName]) {
          if (col.name.toLowerCase().startsWith(colFilter)) {
            suggestions.push({ label: col.name, detail: col.type, value: col.name })
          }
        }
      }
    } else {
      const filter = currentWord.toLowerCase()

      for (const table of this.tableNames) {
        if (table.toLowerCase().startsWith(filter)) {
          suggestions.push({ label: table, detail: "table", value: table })
        }
      }

      for (const kw of this.sqlKeywords) {
        if (kw.toLowerCase().startsWith(filter)) {
          suggestions.push({ label: kw, detail: "keyword", value: kw })
        }
      }

      for (const [alias, table] of Object.entries(this.aliases)) {
        if (alias.startsWith(filter) && alias !== filter) {
          suggestions.push({ label: alias, detail: `→ ${table}`, value: alias })
        }
      }

      const fromMatch = textBeforeCursor.match(/(?:FROM|JOIN)\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?\s+(?:WHERE|AND|OR|ON|SET|HAVING|GROUP|ORDER)\s+/i)
      if (fromMatch) {
        const contextTable = fromMatch[1].toLowerCase()
        if (this.schemaData[contextTable]) {
          for (const col of this.schemaData[contextTable]) {
            if (col.name.toLowerCase().startsWith(filter)) {
              const alreadyAdded = suggestions.some(s => s.label === col.name && s.detail === col.type)
              if (!alreadyAdded) {
                suggestions.push({ label: col.name, detail: col.type, value: col.name })
              }
            }
          }
        }
      }
    }

    return suggestions.slice(0, 20)
  }

  showAutocompleteDropdown(suggestions) {
    const dropdown = this.autocompleteTarget
    this.autocompleteItems = suggestions
    this.autocompleteIndex = 0

    dropdown.innerHTML = suggestions.map((s, i) => `
      <div class="sql-autocomplete-item px-3 py-1.5 cursor-pointer flex justify-between items-center gap-4 text-sm hover:bg-core-purple-200 ${i === 0 ? 'bg-core-purple-200' : ''}"
           data-index="${i}"
           data-action="click->sql-console#clickAutocomplete mouseenter->sql-console#hoverAutocomplete">
        <span class="font-mono text-support-dark-700">${s.label}</span>
        <span class="text-xs text-support-light-500">${s.detail}</span>
      </div>
    `).join("")

    this.positionAutocomplete()
    dropdown.classList.remove("hidden")
    this.showingAutocomplete = true
  }

  positionAutocomplete() {
    const editor = this.editorTarget
    const dropdown = this.autocompleteTarget

    const text = editor.value.substring(0, editor.selectionStart)
    const lines = text.split("\n")
    const currentLine = lines.length - 1
    const currentCol = lines[lines.length - 1].length

    const lineHeight = 20
    const charWidth = 8.4
    const containerRect = editor.parentElement.getBoundingClientRect()

    let top = (currentLine + 1) * lineHeight - editor.scrollTop + 4
    let left = currentCol * charWidth - editor.scrollLeft + 12

    const dropdownHeight = 200
    const dropdownWidth = 250
    if (top + dropdownHeight > containerRect.height) {
      top = Math.max(0, top - dropdownHeight - lineHeight)
    }
    if (left + dropdownWidth > containerRect.width) {
      left = Math.max(0, containerRect.width - dropdownWidth - 10)
    }

    dropdown.style.top = `${top}px`
    dropdown.style.left = `${left}px`
  }

  moveAutocomplete(delta) {
    const items = this.autocompleteTarget.querySelectorAll(".sql-autocomplete-item")
    if (items.length === 0) return

    items[this.autocompleteIndex]?.classList.remove("bg-core-purple-200")
    this.autocompleteIndex = (this.autocompleteIndex + delta + items.length) % items.length
    items[this.autocompleteIndex]?.classList.add("bg-core-purple-200")
    items[this.autocompleteIndex]?.scrollIntoView({ block: "nearest" })
  }

  acceptAutocomplete() {
    if (this.autocompleteIndex < 0 || this.autocompleteIndex >= this.autocompleteItems.length) return

    const item = this.autocompleteItems[this.autocompleteIndex]
    const editor = this.editorTarget
    const cursorPos = editor.selectionStart
    const textBefore = editor.value.substring(0, cursorPos)

    const wordMatch = textBefore.match(/[\w.]+$/)
    if (!wordMatch) return

    const wordStart = cursorPos - wordMatch[0].length
    const parts = wordMatch[0].split(".")
    let replaceStart = wordStart

    if (parts.length === 2) {
      replaceStart = wordStart + parts[0].length + 1
    }

    const before = editor.value.substring(0, replaceStart)
    const after = editor.value.substring(cursorPos)
    editor.value = before + item.value + after
    editor.selectionStart = editor.selectionEnd = replaceStart + item.value.length

    this.hideAutocomplete()
    editor.focus()
  }

  clickAutocomplete(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (!isNaN(index)) {
      this.autocompleteIndex = index
      this.acceptAutocomplete()
    }
  }

  hoverAutocomplete(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (!isNaN(index)) {
      const items = this.autocompleteTarget.querySelectorAll(".sql-autocomplete-item")
      items[this.autocompleteIndex]?.classList.remove("bg-core-purple-200")
      this.autocompleteIndex = index
      items[this.autocompleteIndex]?.classList.add("bg-core-purple-200")
    }
  }

  hideAutocomplete() {
    this.autocompleteTarget.classList.add("hidden")
    this.showingAutocomplete = false
    this.autocompleteIndex = -1
  }

  // Sidebar
  filterTables() {
    const filter = this.tableFilterTarget.value.toLowerCase()
    const items = this.tableListTarget.querySelectorAll(".sql-console-table-item")
    items.forEach(item => {
      const name = item.dataset.tableName.toLowerCase()
      item.style.display = name.includes(filter) ? "" : "none"
    })
  }

  selectTable(event) {
    const tableName = event.currentTarget.dataset.tableName
    const columns = this.schemaData[tableName]

    this.tableListTarget.querySelectorAll(".sql-console-table-item").forEach(el => {
      el.classList.remove("bg-core-purple-200", "font-medium")
    })
    event.currentTarget.classList.add("bg-core-purple-200", "font-medium")

    if (columns) {
      this.showSchema(tableName, columns)
    }
  }

  showSchema(tableName, columns) {
    this.schemaTableNameTarget.textContent = tableName
    this.schemaBodyTarget.innerHTML = columns.map(col => `
      <tr class="border-b border-support-light-300 hover:bg-support-light-300">
        <td class="px-3 py-1 font-mono text-support-dark-700">${col.name}</td>
        <td class="px-3 py-1 text-support-light-600">${col.type}</td>
        <td class="px-3 py-1 text-support-light-600">${col.nullable ? 'YES' : 'NO'}</td>
        <td class="px-3 py-1 text-support-light-500 truncate max-w-xs">${col.default || ''}</td>
      </tr>
    `).join("")
    this.schemaPanelTarget.classList.remove("hidden")
  }

  closeSchema() {
    this.schemaPanelTarget.classList.add("hidden")
  }

  // CSV export via server endpoint
  exportCsv() {
    const query = this.editorTarget.value.trim()
    if (!query) return

    const url = new URL(this.exportUrlValue, window.location.origin)
    url.searchParams.set("query", query)
    window.location.href = url.toString()
  }
}
